import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegHeart, FaUser, FaBars } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import "./HomeHeader.css";
import { colors } from "../../../constants/colors";
import { imagePath } from "../../../constants/imagePath";
import { clearStates } from "../../../constants/globalFunction";
import Drawer from "./drawer";
import { FiShoppingCart } from "react-icons/fi";
import CartDrawer from "../../cart/CartDrawer";

const HomeHeader = ({ fetchDetails }) => {
  const navigate = useNavigate();
  const { cartCount, whishlistCount } = useSelector(
    (state) => state.countState
  );
  const { categories } = useSelector((state) => state.categoryState);
  const newMessageFlag = useSelector((state) => state.supportTicketState.newMessage);
  const userData = useSelector((state) => state.userState.userData);

  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] =
    useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const accountDropdownRef = useRef(null);
  const categoriesDropdownRef = useRef(null);
  const drawerRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);

  // Check if mobile/tablet view
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const toggleAccountDropdown = () => {
    setIsAccountDropdownOpen((prev) => !prev);
    setIsCategoriesDropdownOpen(false);
  };

  const openCategoriesDropdown = () => {
    // Clear any existing timeout
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsCategoriesDropdownOpen(true);
    setIsAccountDropdownOpen(false);
  };

  const closeCategoriesDropdown = () => {
    // Add a small delay to prevent accidental closing
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsCategoriesDropdownOpen(false);
    }, 100);
  };

  const toggleCategoriesDropdown = () => {
    setIsCategoriesDropdownOpen((prev) => !prev);
    setIsAccountDropdownOpen(false);
  };

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openCartDrawer = () => {
    setIsCartDrawerOpen(true);
  };

  const closeCartDrawer = () => {
    setIsCartDrawerOpen(false);
    if (fetchDetails) {
      fetchDetails();
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedOutsideAccount =
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target);
      const clickedOutsideDrawer =
        drawerRef.current && !drawerRef.current.contains(event.target);

      if (clickedOutsideAccount) {
        setIsAccountDropdownOpen(false);
      }

      if (isDrawerOpen && clickedOutsideDrawer) {
        closeDrawer();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Cleanup timeout on unmount
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, [isDrawerOpen]);

  const onLogout = async () => {
    try {
      await clearStates();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleNavigation = (path, state = null) => {
    if (state) {
      navigate(path, state);
    } else {
      navigate(path);
    }
    closeDrawer();
  };

  // Get first 2 categories for desktop navigation
  const desktopCategories = categories?.slice(0, 5) || [];

  return (
    <header className="homeHeader">
      {/* Mobile Hamburger Menu */}
      {isMobile && (
        <button
          className="homeHeader__hamburger"
          onClick={toggleDrawer}
          aria-label="Menu"
        >
          <FaBars size={20} color={colors.primary} />
        </button>
      )}

      {/* Logo - Centered on mobile, left on desktop */}
      <div
        className={`homeHeader__brand ${isMobile ? "homeHeader__brand--mobile" : ""
          }`}
        onClick={() => navigate("/")}
      >
        <img
          src={imagePath.appLogo}
          alt="Codeinfoway"
          className="homeHeader__logo"
        />
      </div>

      {/* Desktop Navigation Links */}
      {!isMobile && (
        <nav className="homeHeader__navigation">
          <button className="homeHeader__navLink" onClick={() => navigate("/")}>
            Home
          </button>

          <div
            className="homeHeader__navDropdown"
            ref={categoriesDropdownRef}
            onMouseEnter={openCategoriesDropdown}
            onMouseLeave={closeCategoriesDropdown}
          >
            <button
              className="homeHeader__navLink homeHeader__navLink--dropdown"
              onClick={() => navigate("/search")}
            >
              All
            </button>
            <div
              className={`homeHeader__navDropdownMenu ${isCategoriesDropdownOpen ? "show" : ""
                }`}
              onMouseEnter={() => {
                if (dropdownTimeoutRef.current) {
                  clearTimeout(dropdownTimeoutRef.current);
                  dropdownTimeoutRef.current = null;
                }
              }}
            >
              {categories?.map((cat) => (
                <button
                  key={cat?._id}
                  className="homeHeader__navDropdownItem"
                  onClick={() => {
                    navigate(`/category?category_id=${cat?._id}`, {
                      state: { category: cat },
                    });
                    setIsCategoriesDropdownOpen(false);
                  }}
                >
                  {cat?.name}
                </button>
              ))}
            </div>
          </div>

          {desktopCategories.map((cat) => (
            <button
              key={cat?._id}
              className="homeHeader__navLink"
              onClick={() =>
                navigate(`/category?category_id=${cat?._id}`, { state: { category: cat } })
              }
            >
              {cat?.name}
            </button>
          ))}
        </nav>
      )}

      {/* Right Side Actions */}
      <div className="homeHeader__actions">
        <button
          className="homeHeader__iconBtn"
          aria-label="Search"
          onClick={() => navigate("/search")}
        >
          <FiSearch size={18} color={colors.primary} />
        </button>

        <button
          className="homeHeader__iconBtn homeHeader__badgeWrapper"
          aria-label="Wishlist"
          onClick={() => navigate("/whishlist")}
        >
          <FaRegHeart size={18} color={colors.primary} />
          {
            userData && (
              <span className="homeHeader__badge">{whishlistCount}</span>
            )
          }
        </button>

        <button
          className="homeHeader__iconBtn homeHeader__badgeWrapper"
          aria-label="Cart"
          onClick={openCartDrawer}
        >
          <FiShoppingCart size={18} color={colors.primary} />
          {
            userData && (
              <span className="homeHeader__badge">{cartCount}</span>
            )
          }

        </button>
        {!isMobile && (
          <div className="homeHeader__account" ref={accountDropdownRef}>
            <button
              className="homeHeader__iconBtn"
              onClick={toggleAccountDropdown}
              aria-haspopup="menu"
              aria-expanded={isAccountDropdownOpen}
              aria-label="My Account"
            >
              <FaUser size={18} color={colors.primary} />
            </button>
            {isAccountDropdownOpen && (
              <div className="homeHeader__dropdown" role="menu">
                {userData && (
                  <>
                    <button
                      className="homeHeader__dropdownItem"
                      onClick={() => {
                        navigate("/profile");
                      }}
                    >
                      Profile
                    </button>
                    <button
                      className="homeHeader__dropdownItem"
                      onClick={() => {
                        navigate("/change-password");
                      }}
                    >
                      Change Password
                    </button>
                  </>
                )}

                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => {
                    navigate("/orders");
                  }}
                >
                  My Order
                </button>
                {userData && (
                  <>
                    <div className="homeHeader__dropdownItem" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <button
                        onClick={() => navigate("/support-tickets")}
                        style={{ background: 'none', border: 'none', padding: 0 }}
                      >
                        My Tickets
                      </button>
                      {Number(newMessageFlag) === 1 && (
                        <span className="homeHeader__newMsg">New Reply</span>
                      )}
                    </div>
                  </>
                )}

                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => navigate("/notifications")}
                >
                  Notifications
                </button>
                {userData && userData?.user_type === "wholeseller" && (
                  <button
                    className="homeHeader__dropdownItem"
                    onClick={() => {
                      navigate("/bank-details");
                    }}
                  >
                    Bank Details
                  </button>
                )}
                <button
                  className="homeHeader__dropdownItem"
                  onClick={() => {
                    window.open("/contact-us", "_blank");
                  }}
                >
                  Contact Us
                </button>
                {userData ? (
                  <button
                    className="homeHeader__dropdownItem homeHeader__logout"
                    onClick={onLogout}
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    className="homeHeader__dropdownItem"
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          categories={categories}
          userData={userData}
          isCategoriesDropdownOpen={isCategoriesDropdownOpen}
          isAccountDropdownOpen={isAccountDropdownOpen}
          toggleCategoriesDropdown={toggleCategoriesDropdown}
          toggleAccountDropdown={toggleAccountDropdown}
          handleNavigation={handleNavigation}
          onLogout={onLogout}
          openCartDrawer={openCartDrawer}
          newMessageFlag={newMessageFlag}
        />
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartDrawerOpen}
        onClose={closeCartDrawer}
      />
    </header>
  );
};

export default HomeHeader;
